import React from 'react';
import {
  MoreVertical,
  Users,
  GraduationCap,
  Pencil,
  Power,
  Eye,
  Trophy,
  ArrowLeftRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { CLUB_STATUS } from '@/types/club.model';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface ClubRowMenuProps {
  role: 'owner' | 'future_owner' | 'manager' | 'instructor';
  status: CLUB_STATUS;
  onManagers: () => void;
  onInstructors: () => void;
  onEdit: () => void;
  onDetails: () => void;
  onRanking: () => void;
  onToggleStatus: () => void;
  onTransfer: () => void;
}

export const ClubRowMenu: React.FC<ClubRowMenuProps> = ({
  role,
  status,
  onManagers,
  onInstructors,
  onEdit,
  onDetails,
  onRanking,
  onToggleStatus,
  onTransfer,
}) => {
  const showToggle = status !== CLUB_STATUS.IN_PROGRESS;
  const isDisabling = status === CLUB_STATUS.ENABLED;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Acciones del club"
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-[9px]',
            'border border-gc-line bg-white text-gc-muted',
            'hover:bg-gc-line2 transition-colors',
          )}
        >
          <MoreVertical size={18} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="min-w-[196px] rounded-xl border-gc-line p-1.5 shadow-[0_12px_32px_rgba(11,18,32,0.14)]"
      >
        {role === 'owner' ? (
          <>
            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
              onSelect={onManagers}
            >
              <Users size={15} className="text-gc-muted" />
              Administrar managers
            </DropdownMenuItem>

            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
              onSelect={onInstructors}
            >
              <GraduationCap size={15} className="text-gc-muted" />
              Administrar profesores
            </DropdownMenuItem>

            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
              onSelect={onEdit}
            >
              <Pencil size={15} className="text-gc-muted" />
              Editar club
            </DropdownMenuItem>

            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
              onSelect={onRanking}
            >
              <Trophy size={15} className="text-gc-muted" />
              Ver ranking
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {showToggle && (
              <DropdownMenuItem
                className={cn(
                  'gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2',
                  isDisabling && 'text-gc-danger focus:text-gc-danger',
                )}
                onSelect={onToggleStatus}
              >
                <Power
                  size={15}
                  className={cn(isDisabling ? 'text-gc-danger' : 'text-gc-muted')}
                />
                {isDisabling ? 'Deshabilitar club' : 'Habilitar club'}
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2 text-gc-danger focus:text-gc-danger"
              onSelect={onTransfer}
            >
              <ArrowLeftRight size={15} className="text-gc-danger" />
              Transferir club
            </DropdownMenuItem>
          </>
        ) : role === 'future_owner' ? (
          <DropdownMenuItem
            className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
            onSelect={onEdit}
          >
            <Pencil size={15} className="text-gc-muted" />
            Editar club
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
              onSelect={onInstructors}
            >
              <GraduationCap size={15} className="text-gc-muted" />
              Administrar profesores
            </DropdownMenuItem>

            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
              onSelect={onDetails}
            >
              <Eye size={15} className="text-gc-muted" />
              Ver detalles
            </DropdownMenuItem>

            <DropdownMenuItem
              className="gap-2.5 text-[13.5px] font-medium cursor-pointer rounded-lg px-2.5 py-2"
              onSelect={onRanking}
            >
              <Trophy size={15} className="text-gc-muted" />
              Ver ranking
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
